import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, catchError, throwError } from 'rxjs';
import {
  PlacementDocRequest,
  PlacementDocuments,
} from '../modules/placement/interfaces/placement-documents';
import { GenerateOtPtDocuments } from '../modules/student-profile/interfaces/generate-ot-pt-documents';
import { ApiResponse } from '../shared/interfaces/api-response';
import { IAppConfig } from '../shared/interfaces/app-config';
import { UpdateDocumentStatuses } from '../shared/interfaces/document';
import { AttendeeList } from '../shared/interfaces/meetingAttendee';
import { StudentDocument } from '../shared/interfaces/student-document';
import { AppConfigService } from './app-config.service';
import { assertTrustedUrl } from '../shared/functions/validate-url';

@Injectable({
  providedIn: 'root',
})
export class DocumentService {
  config: IAppConfig;

  headers: HttpHeaders = new HttpHeaders({
    enctype: 'multipart/form-data',
    Accept: 'application/json',
  });

  constructor(
    private http: HttpClient,
    private appConfigService: AppConfigService,
  ) {
    this.config = this.appConfigService.getConfig();
  }

  private sanitizePathSegment(value: string | number): string {
    return encodeURIComponent(String(value ?? '').replace(/[^A-Za-z0-9_-]/g, ''));
  }

  private assertSafeIdentifier(value: string | number): string {
    const candidate: string = String(value ?? '').trim();
    if (!/^[A-Za-z0-9_-]{1,64}$/.test(candidate)) {
      throw new Error('Invalid identifier');
    }
    return candidate;
  }

  public uploadDocument(formData: FormData): Observable<number[]> {
    return this.http
      .post<number[]>(
        `${this.config.apiUrl}/Document/UploadDocuments`,
        formData,
        {
          headers: this.headers,
        }
      )
      .pipe(catchError(this.handleError));
  }

  public uploadVarianceDocuments(formData: FormData): Observable<number[]> {
    return this.http
      .post<number[]>(`${this.config.apiUrl}/Document/UploadDoc`, formData, {
        headers: this.headers,
      })
      .pipe(catchError(this.handleError));
  }

  public deleteDocument(docId: number, comment: string): Observable<boolean> {
    const url = assertTrustedUrl(
      `${this.config.apiUrl}/Document/DeleteDocument/${encodeURIComponent(String(docId))}/${encodeURIComponent(comment)}`,
      this.config.apiUrl
    );
    return this.http
      .post<boolean>(url, null)
      .pipe(catchError(this.handleError));
  }

  public getDocument(docId: number): Observable<Uint8Array> {
    const url = assertTrustedUrl(
      `${this.config.apiUrl}/Document/GetDocument/${encodeURIComponent(String(docId))}`,
      this.config.apiUrl
    );
    return this.http
      .get<Uint8Array>(url)
      .pipe(catchError(this.handleError));
  }

  public getSESISDocument(docId: number): Observable<Uint8Array> {
    return this.http
      .get<Uint8Array>(`${this.config.apiUrl}/Document/GetSESISDocumentById?docId=${docId}`)
      .pipe(catchError(this.handleError));
  }

  public getStudentDocuments(studentSk: string): Observable<StudentDocument[]> {
    const url = assertTrustedUrl(
      `${this.config.apiUrl}/Document/GetStudentDocuments/${encodeURIComponent(studentSk)}`,
      this.config.apiUrl
    );
    return this.http
      .get<StudentDocument[]>(url)
      .pipe(catchError(this.handleError));
  }

  public getStudentDocumentsByID(
    studentId: string
  ): Observable<StudentDocument[]> {
    const url = assertTrustedUrl(
      `${this.config.apiUrl}/Document/GetStudentDocumentsByID/${encodeURIComponent(studentId)}`,
      this.config.apiUrl
    );
    return this.http
      .get<StudentDocument[]>(url)
      .pipe(catchError(this.handleError));
  }

  public getKIPStudentDocuments(
    studentId: string
  ): Observable<StudentDocument[]> {
    const url = assertTrustedUrl(
      `${this.config.apiUrl}/Document/GetKIPStudentDocuments/${encodeURIComponent(studentId)}`,
      this.config.apiUrl
    );
    return this.http
      .get<StudentDocument[]>(url)
      .pipe(catchError(this.handleError));
  }

  public getCaseActionDocuments(
    studentSk: string,
    caseActionSk: string
  ): Observable<StudentDocument[]> {
    const url = assertTrustedUrl(
      `${this.config.apiUrl}/Document/GetCaseActionDocuments/${encodeURIComponent(studentSk)}/${encodeURIComponent(caseActionSk)}`,
      this.config.apiUrl
    );
    return this.http
      .get<StudentDocument[]>(url)
      .pipe(catchError(this.handleError));
  }

  public getAttendanceDocument(
    meetingSk: string,
    attendeesList: AttendeeList
  ): Observable<Uint8Array> {
    return this.http
      .post<Uint8Array>(
        `${this.config.apiUrl}/DocumentGen/AttendanceDocument?meetingSk=${meetingSk}`,
        attendeesList
      )
      .pipe(catchError(this.handleError));
  }

  public updateDocumentStatuses(
    updateDocumentStatusesData: UpdateDocumentStatuses[]
  ): Observable<ApiResponse<UpdateDocumentStatuses>> {
    return this.http
      .post<ApiResponse<UpdateDocumentStatuses>>(
        `${this.config.apiUrl}/Document/UpdateDocumentStatuses`,
        updateDocumentStatusesData
      )
      .pipe(catchError(this.handleError));
  }

  public deleteDocumentsById(
    eiId: string,
    comment: string,
    studentSk: string
  ): Observable<boolean> {
    const url = assertTrustedUrl(
      `${this.config.apiUrl}/Document/DeleteFolder/${encodeURIComponent(eiId)}/${encodeURIComponent(studentSk)}`,
      this.config.apiUrl
    );
    return this.http
      .post<boolean>(url, null)
      .pipe(catchError(this.handleError));
  }

  public generateOTPTDocument(
    docObj: GenerateOtPtDocuments
  ): Observable<Uint8Array> {
    const params: HttpParams = new HttpParams({ fromObject: { ...docObj } });

    return this.http
      .get<Uint8Array>(
        `${this.config.apiUrl}/DocumentGen/GenerateOTPTDocument`,
        {
          params,
        }
      )
      .pipe(catchError(this.handleError));
  }

  getPlacementDocuments(
    getDocPayload: PlacementDocRequest
  ): Observable<PlacementDocuments> {
    return this.http
      .post<PlacementDocuments>(
        `${this.config.apiUrl}/Document/GetPlacementDocuments`,
        getDocPayload
      )
      .pipe(catchError(this.handleError));
  }

  createAndGetDoc(nycId: string, caseActionSk: string, moduleType: number, documentType: string | number, studentSk: string): Observable<Uint8Array> {
    const safeNycId: string = encodeURIComponent(this.assertSafeIdentifier(nycId));
    const safeCaseActionSk: string = encodeURIComponent(this.assertSafeIdentifier(caseActionSk));
    const safeModuleType: string = encodeURIComponent(this.assertSafeIdentifier(moduleType));
    const safeDocumentType: string = encodeURIComponent(this.assertSafeIdentifier(documentType));
    const safeStudentSk: string = encodeURIComponent(this.assertSafeIdentifier(studentSk));
    const url = assertTrustedUrl(
      `${this.config.apiUrl}/DocumentGen/CreateAndGetDocument/${safeNycId}/${safeCaseActionSk}/${safeModuleType}/${safeDocumentType}/${safeStudentSk}`,
      this.config.apiUrl
    );

    return this.http
      .get<Uint8Array>(url)
      .pipe(catchError(this.handleError));
  }

  // Error handling
  handleError(error: HttpErrorResponse): Observable<never> {
    let errorMessage: string = '';
    if (error.error instanceof ErrorEvent) {
      // Get client-side error
      errorMessage = error.error.message;
    } else {
      // Get server-side error
      errorMessage = `Error Code: ${error.status}\nMessage: ${error.message}`;
    }
    //window.alert(errorMessage);
    return throwError(() => {
      return errorMessage;
    });
  }

  public generateSHPDocument(payload: {
    nycId: string | number;
    contactName: string;
    contactEmail: string;
    referralSk: string;
    currentDate: string;
  }): Observable<Uint8Array> {
    const params: HttpParams = new HttpParams({
      fromObject: {
        nycId: payload.nycId.toString(),
        contactName: payload.contactName,
        contactEmail: payload.contactEmail,
        referralSk: payload.referralSk,
        currentDate: payload.currentDate,
      },
    });

    return this.http
      .get<Uint8Array>(`${this.config.apiUrl}/DocumentGen/GenerateSHPDocument`, {
        params,
      })
      .pipe(catchError(this.handleError));
  }

  public generateDEP1Document(nycId: string | number, iepSk: string | null): Observable<Uint8Array> {
    const params: HttpParams = new HttpParams({
      fromObject: { nycId: nycId.toString(), ...(iepSk ? { iepSk } : {}) },
    });

    return this.http
      .get<Uint8Array>(`${this.config.apiUrl}/DocumentGen/GenerateDEP1Document`, { params })
      .pipe(catchError(this.handleError));
  }
}